import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CreateTestimonialRequestRequest } from '@clareefai/types';
import { Avatar, MarkdownEditor, MarkdownRenderer } from '@clareefai/ui';
import { useGetPromoterByIdQuery, useGetPromotersQuery, PromoterResponse } from '../../store/promoterApi';
import { useCreateTestimonialRequestMutation, useGetUseCaseQuestionsQuery, useGenerateUseCaseForRequestMutation } from '../../store/vendorApi';
import { ChevronLeft, ChevronRight, Check, User, Building, Star, Award, Wand2, Edit3, Lightbulb, FileText, Sparkles, Loader } from 'lucide-react';

const TOTAL_STEPS = 5;

type AnswerType = 'one_selected' | 'many_selected' | 'free_text';

interface UseCaseQuestion {
  id: string;
  question: string;
  choices: string[];
  selectedOptions: string[];
  answerType?: AnswerType;
  freeText?: string;
}

interface UseCaseRequestFormProps {
  onClose: () => void;
  initialPromoterId?: string;
}

interface Review {
  id: string;
  author: string;
  company: string;
  rating: number;
  comment: string;
  product: string;
  sector: string;
  companySize: string;
  date: string;
}

const UseCaseRequestForm: React.FC<UseCaseRequestFormProps> = ({ onClose, initialPromoterId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: promotersData = [], isLoading: loadingPromoters } = useGetPromotersQuery();
  // Launched from a promoter profile → skip the picker and land on step 2.
  const [currentStep, setCurrentStep] = useState(initialPromoterId ? 2 : 1);
  const [selectedPromoterId, setSelectedPromoterId] = useState<string | null>(initialPromoterId ?? null);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [useCaseMode, setUseCaseMode] = useState<'ai' | 'manual'>('ai');
  const [useCaseContent, setUseCaseContent] = useState('');
  // Structured metadata captured from the AI generation response. We keep it
  // alongside the editable markdown and roundtrip it on submit so the resulting
  // Testimonial has the same shape as one created via the promoter-direct flow.
  const [useCaseMetadata, setUseCaseMetadata] = useState<{
    title?: string;
    description?: string;
    keyBenefits?: string[];
    industry?: string;
    companySize?: string;
  }>({});
  // Manual / free-form inputs. Required when useCaseMode === 'manual' so the
  // submitted Testimonial has the same structured fields as the AI path.
  const [manualTitle, setManualTitle] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualKeyBenefits, setManualKeyBenefits] = useState<string[]>(['']);
  const [hasGeneratedContent, setHasGeneratedContent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Vendor use-case questions for AI generation (step 4)
  const { data: apiQuestions = [], isLoading: loadingQuestions } = useGetUseCaseQuestionsQuery();
  const baseQuestions = useMemo<UseCaseQuestion[]>(() => {
    return [...apiQuestions]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(q => ({
        id: q.id,
        question: q.question,
        choices: q.choices ?? [],
        selectedOptions: [] as string[],
        answerType: (q.answer_type ?? 'many_selected') as AnswerType,
        freeText: ''
      }));
  }, [apiQuestions]);
  const [questions, setQuestions] = useState<UseCaseQuestion[]>(baseQuestions);
  useEffect(() => {
    if (baseQuestions.length > 0) setQuestions(baseQuestions);
  }, [baseQuestions]);

  const [generateUseCaseForRequest, { isLoading: isGenerating, error: generateError }] = useGenerateUseCaseForRequestMutation();

  // Map promoters to include name property and filter eligible ones
  const promoters = useMemo(() => {
    return promotersData
      .filter(p => {
        // Filter promoters that are eligible for use case requests
        // Include promoters with status that indicates they're active/available
        const eligibleStatuses = ['review', 'testimonial_ready', 'testimonial_done', 'available'];
        return !p.status || eligibleStatuses.includes(p.status.toLowerCase());
      })
      .map(p => ({
        ...p,
        name: `${p.firstName} ${p.lastName}`
      }));
  }, [promotersData]);

  // Derive the selected promoter from the full list (not the eligibility-filtered
  // `promoters`), so a promoter pre-selected from their profile still resolves.
  const selectedPromoter = useMemo<PromoterResponse | null>(() => {
    if (!selectedPromoterId) return null;
    const found = promotersData.find(p => p.id === selectedPromoterId);
    return found ? { ...found, name: `${found.firstName} ${found.lastName}` } : null;
  }, [promotersData, selectedPromoterId]);

  // Get promoter details including testimonials
  const { data: promoterDetails, isLoading: loadingPromoter } = useGetPromoterByIdQuery(
    selectedPromoterId || '',
    { skip: !selectedPromoterId }
  );

  const useCaseCount = promoterDetails?.testimonials?.filter(t => t.type === 'USE_CASE').length ?? 0;
  const atUseCaseLimit = useCaseCount >= 3;

  // Create testimonial request mutation
  const [createRequest, { isLoading: isSubmitting }] = useCreateTestimonialRequestMutation();

  // Get reviews from promoter's testimonials
  const getPromoterReviews = (): Review[] => {
    if (!promoterDetails?.testimonials) return [];
    
    return promoterDetails.testimonials
      .filter(t => t.type === 'REVIEW' && (t.status === 'PUBLISHED' || t.status === 'FEATURED'))
      .map(t => ({
        id: t.id,
        author: `${promoterDetails.firstName} ${promoterDetails.lastName}`,
        company: promoterDetails.company || '',
        rating: t.rating || 5,
        comment: t.content || '',
        product: t.productId || '',
        sector: promoterDetails.industrySectors?.[0] || '',
        companySize: promoterDetails.companySize || '',
        date: t.createdAt
      }));
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleOptionToggle = (questionId: string, option: string) => {
    setQuestions(prev => prev.map(q => {
      if (q.id !== questionId) return q;
      const isSelected = q.selectedOptions.includes(option);
      const isOneSelected = q.answerType === 'one_selected';
      return {
        ...q,
        selectedOptions: isSelected
          ? q.selectedOptions.filter(o => o !== option)
          : isOneSelected
            ? [option]
            : [...q.selectedOptions, option]
      };
    }));
  };

  const setQuestionFreeText = (questionId: string, value: string) => {
    setQuestions(prev => prev.map(q =>
      q.id === questionId ? { ...q, freeText: value } : q
    ));
  };

  const generateAIUseCase = async () => {
    if (!selectedPromoter?.id) {
      setError(t('useCases.requestPage.selectPromoterFirst'));
      return;
    }
    const answers = questions.map(q =>
      q.answerType === 'free_text'
        ? { question_id: q.id, free_text: (q.freeText ?? '').trim() }
        : { question_id: q.id, selected_choices: q.selectedOptions }
    );
    const allValid = questions.every(q =>
      q.answerType === 'free_text'
        ? (q.freeText ?? '').trim().length > 0
        : q.selectedOptions.length > 0
    );
    if (!allValid) {
      setError(questions.some(q => q.answerType === 'free_text')
        ? t('useCases.requestPage.answerAllQuestions')
        : t('useCases.requestPage.selectOnePerQuestion'));
      return;
    }
    setError(null);
    try {
      const result = await generateUseCaseForRequest({
        promoter_id: selectedPromoter.id,
        answers,
        product_id: selectedReview?.product ?? undefined
      }).unwrap();
      setUseCaseContent(result.generated_content ?? '');
      setUseCaseMetadata({
        title: result.title,
        description: result.description,
        keyBenefits: result.key_benefits,
        industry: result.industry,
        companySize: result.company_size,
      });
      setHasGeneratedContent(true);
    } catch (err: any) {
      setError(err?.data?.message || err?.message || t('useCases.requestPage.generateFailed'));
    }
  };

  const handleSubmit = async () => {
    if (!selectedPromoter || !useCaseContent.trim()) {
      setError(t('useCases.requestPage.completeRequiredFields'));
      return;
    }
    if (atUseCaseLimit) {
      setError(t('useCases.requestPage.atLimit'));
      return;
    }

    setError(null);

    try {
      const isManual = useCaseMode === 'manual';
      const request: CreateTestimonialRequestRequest = {
        promoterId: selectedPromoter.id!,
        type: 'USE_CASE',
        content: useCaseContent,
        reviewId: isManual ? undefined : selectedReview?.id,
        productId: selectedReview?.product,
        use_case_title: isManual ? manualTitle.trim() : useCaseMetadata.title,
        use_case_description: isManual ? manualDescription.trim() : useCaseMetadata.description,
        use_case_key_benefits: isManual ? trimmedManualBenefits : useCaseMetadata.keyBenefits,
        use_case_industry: isManual ? undefined : useCaseMetadata.industry,
        use_case_company_size: isManual ? undefined : useCaseMetadata.companySize,
        use_case_source: isManual ? 'FREE_FORM' : 'AI',
      };

      await createRequest(request).unwrap();
      
      // Navigate to use cases page on success
      navigate('/use-cases');
      onClose();
    } catch (err: any) {
      const message =
        err?.data?.message ??
        (typeof err?.data === 'string' ? err.data : null) ??
        err?.error ??
        err?.message ??
        t('useCases.requestPage.submitFailed');
      setError(message);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((step) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold text-sm ${
            step <= currentStep 
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 border-purple-600 text-white' 
              : 'border-gray-300 text-gray-400'
          }`}>
            {step < currentStep ? <Check className="w-5 h-5" /> : step}
          </div>
          {step < TOTAL_STEPS && (
            <div className={`w-12 h-0.5 ${
              step < currentStep ? 'bg-gradient-to-r from-purple-600 to-indigo-600' : 'bg-gray-300'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">Select Promoter</h3>
        <p className="text-gray-600">Choose the promoter for the use case</p>
      </div>

      {loadingPromoters ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-purple-600" />
          <span className="ml-3 text-gray-600">Loading promoters...</span>
        </div>
      ) : promoters.length === 0 ? (
        <div className="text-center py-12">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No promoters available</h4>
          <p className="text-gray-600">You don't have any eligible promoters for use case requests.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[500px] overflow-y-auto">
          {promoters.map((promoter) => (
          <div
            key={promoter.id}
            onClick={() => setSelectedPromoterId(promoter.id ?? null)}
            className={`relative p-4 border-2 rounded-xl cursor-pointer transition-colors duration-200 ${
              selectedPromoter?.id === promoter.id
                ? 'border-purple-500 bg-purple-50 shadow-md'
                : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
            }`}
          >
            <div className="flex flex-col items-center text-center space-y-3">
              <Avatar
                src={promoter.avatar}
                name={promoter.name}
                size="lg"
                className="border-4 border-white shadow-md"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-900 text-lg truncate">{promoter.name}</h4>
                <p className="text-sm text-gray-600 flex items-center justify-center truncate">
                  <Building className="w-4 h-4 mr-1" />
                  {promoter.company}
                </p>
                <div className="flex items-center justify-center space-x-4 mt-2">
                  <div className="flex items-center text-sm text-gray-500">
                    <Star className="w-4 h-4 mr-1 text-yellow-500" />
                    {promoter.prospectsReferred || 0}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Award className="w-4 h-4 mr-1 text-green-500" />
                    €{promoter.totalRewards || 0}
                  </div>
                </div>
                <div className="mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    promoter.status === 'review' ? 'bg-blue-100 text-blue-800' :
                    promoter.status === 'testimonial_ready' ? 'bg-green-100 text-green-800' :
                    promoter.status === 'testimonial_done' ? 'bg-purple-100 text-purple-800' :
                    promoter.status === 'available' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {promoter.status === 'review' ? 'Reviewed' :
                     promoter.status === 'testimonial_ready' ? 'Testimonial Ready' :
                     promoter.status === 'testimonial_done' ? 'Testimonial Done' :
                     promoter.status === 'available' ? 'Available' :
                     promoter.status || 'Active'}
                  </span>
                </div>
              </div>
            </div>
            {selectedPromoter?.id === promoter.id && (
              <div className="absolute top-3 right-3 w-7 h-7 bg-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
        ))}
        </div>
      )}

      {selectedPromoter && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Lightbulb className="w-6 h-6 text-purple-600" />
            <div>
              <h4 className="font-semibold text-purple-900">Selected Promoter</h4>
              <p className="text-purple-700">
                <strong>{selectedPromoter.firstName} {selectedPromoter.lastName}</strong> from {selectedPromoter.company}
              </p>
              <p className="text-sm text-purple-600 mt-1">
                Total rewards: €{(selectedPromoter.totalRewards || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => {
    const promoterReviews = selectedPromoter ? getPromoterReviews() : [];

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Select Review (Optional)</h3>
          <p className="text-gray-600">Optionally choose a review to base the use case on, or continue without one</p>
        </div>

        {loadingPromoter ? (
          <div className="text-center py-12">
            <Loader className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading reviews...</p>
          </div>
        ) : promoterReviews.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No reviews available</h4>
            <p className="text-gray-600 mb-4">This promoter hasn't left any published reviews yet. You can continue without a review—use the Next button below to proceed.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[500px] overflow-y-auto">
            {promoterReviews.map((review) => (
              <div
                key={review.id}
                onClick={() => setSelectedReview(review)}
                className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                  selectedReview?.id === review.id
                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                      {review.author.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg">{review.author}</h4>
                      <p className="text-base text-gray-600">{review.company}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <div className="flex">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <span className="text-base text-gray-600 ml-2">({review.rating}/5)</span>
                  </div>

                  <p className="text-gray-700 italic text-base leading-relaxed">"{review.comment}"</p>

                  <div className="grid grid-cols-2 gap-4 text-base">
                    <div>
                      <span className="font-medium text-gray-600">Product:</span>
                      <p className="text-gray-900">{review.product}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Sector:</span>
                      <p className="text-gray-900">{review.sector}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Company Size:</span>
                      <p className="text-gray-900">{review.companySize}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Date:</span>
                      <p className="text-gray-900">{new Date(review.date).toLocaleDateString('en-US')}</p>
                    </div>
                  </div>
                </div>

                {selectedReview?.id === review.id && (
                  <div className="absolute top-4 right-4 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {promoterReviews.length > 0 && (
          <div className="text-center">
            <button
              type="button"
              onClick={handleNext}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium hover:underline"
            >
              Skip — continue without review
            </button>
          </div>
        )}

        {selectedReview && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <Star className="w-6 h-6 text-blue-600" />
              <div>
                <h4 className="font-semibold text-blue-900">Selected Review</h4>
                <p className="text-blue-700">
                  <strong>{selectedReview.product}</strong> - {selectedReview.rating}/5 stars
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  From {selectedReview.company} ({selectedReview.sector})
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">Create Use Case</h3>
        <p className="text-gray-600">Choose how you want to create the use case content</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          type="button"
          onClick={() => setUseCaseMode('ai')}
          className={`p-6 rounded-xl border-2 transition-all duration-200 ${
            useCaseMode === 'ai'
              ? 'border-purple-500 bg-purple-50 shadow-lg'
              : 'border-gray-200 hover:border-purple-300'
          }`}
        >
          <div className="text-center">
            <Wand2 className="w-12 h-12 text-purple-600 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Generate with AI</h4>
            <p className="text-sm text-gray-600">Answer questions and our AI will create the use case</p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setUseCaseMode('manual')}
          className={`p-6 rounded-xl border-2 transition-all duration-200 ${
            useCaseMode === 'manual'
              ? 'border-blue-500 bg-blue-50 shadow-lg'
              : 'border-gray-200 hover:border-blue-300'
          }`}
        >
          <div className="text-center">
            <Edit3 className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Write Manually</h4>
            <p className="text-sm text-gray-600">Write your own use case content from scratch</p>
          </div>
        </button>
      </div>
    </div>
  );

  const allQuestionsAnswered = questions.length > 0 && questions.every(q =>
    q.answerType === 'free_text'
      ? (q.freeText ?? '').trim().length > 0
      : q.selectedOptions.length > 0
  );

  const renderStep4 = () => {
    if (useCaseMode === 'manual') {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Write Use Case Content</h3>
            <p className="text-gray-600">Fill the title, summary, and key benefits — these are what the company page card displays.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={manualTitle}
              onChange={(e) => setManualTitle(e.target.value)}
              maxLength={120}
              placeholder="e.g. Vandomel Consulting Streamlines Legal Workflows with Clareefai"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Short description <span className="text-gray-400">(~10 words)</span>
            </label>
            <input
              type="text"
              value={manualDescription}
              onChange={(e) => setManualDescription(e.target.value)}
              placeholder="One sentence summarizing the achievement"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {manualDescription.trim() ? `${manualDescription.trim().split(/\s+/).length} words` : '0 words'} · max 12
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Key benefits <span className="text-gray-400">(1–4, 1–6 words each)</span>
            </label>
            <div className="space-y-2">
              {manualKeyBenefits.map((b, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    value={b}
                    onChange={(e) =>
                      setManualKeyBenefits((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))
                    }
                    placeholder="e.g. Improved workflow automation"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setManualKeyBenefits((prev) => prev.filter((_, idx) => idx !== i))}
                    className="px-3 py-2 text-sm text-gray-500 hover:text-red-600"
                    aria-label="Remove benefit"
                  >
                    ×
                  </button>
                </div>
              ))}
              {manualKeyBenefits.length < 4 && (
                <button
                  type="button"
                  onClick={() => setManualKeyBenefits((prev) => [...prev, ''])}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + Add benefit
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Use case content
            </label>
            <MarkdownEditor
              value={useCaseContent}
              onChange={setUseCaseContent}
              placeholder="Write your use case content here..."
              minHeight="360px"
            />
          </div>
        </div>
      );
    }

    // AI mode: either show questions + Generate, or show generated content editor
    if (hasGeneratedContent) {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Generated Use Case</h3>
            <p className="text-gray-600">Review and edit the generated content, then go to the next step to send</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Generated content (editable)
            </label>
            <MarkdownEditor
              value={useCaseContent}
              onChange={setUseCaseContent}
              minHeight="360px"
            />
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Answer Questions</h3>
          <p className="text-gray-600">Select at least one option per question; then we'll generate the use case with AI</p>
        </div>

        {loadingQuestions ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-purple-600" />
            <span className="ml-3 text-gray-600">Loading questions...</span>
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-8 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-amber-800">No use case questions configured. Add questions in vendor settings or write the use case manually.</p>
          </div>
        ) : (
          <>
            <div className="space-y-6 max-h-[400px] overflow-y-auto">
              {questions.map((q) => (
                <div key={q.id} className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                  <p className="font-medium text-gray-900 mb-3">{q.question}</p>
                  {q.answerType === 'free_text' ? (
                    <textarea
                      value={q.freeText ?? ''}
                      onChange={(e) => setQuestionFreeText(q.id, e.target.value)}
                      placeholder="Enter your answer..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {q.choices.map((choice) => (
                        <button
                          key={choice}
                          type="button"
                          onClick={() => handleOptionToggle(q.id, choice)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            q.selectedOptions.includes(choice)
                              ? 'bg-purple-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:border-purple-400'
                          }`}
                        >
                          {choice}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-center pt-4">
              <button
                type="button"
                onClick={generateAIUseCase}
                disabled={isGenerating || !allQuestionsAnswered}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    <span>Generate Use Case</span>
                  </>
                )}
              </button>
            </div>
            {(error || generateError) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm">
                {error || ('data' in generateError && (generateError.data as { message?: string })?.message) || 'Generation failed'}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderStep5 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">Review & Send Request</h3>
        <p className="text-gray-600">Confirm details and send the use case request to the promoter</p>
      </div>

      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
          <Lightbulb className="w-5 h-5 mr-2 text-purple-600" />
          Use Case Summary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-base">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Promoter:</span>
              <span className="text-gray-900">{selectedPromoter?.firstName} {selectedPromoter?.lastName}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Company:</span>
              <span className="text-gray-900">{selectedPromoter?.company}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Product:</span>
              <span className="text-gray-900">{selectedReview?.product ?? '—'}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Review:</span>
              <span className="text-gray-900">{selectedReview ? `${selectedReview.rating}/5 stars` : '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Mode:</span>
              <span className="text-gray-900">{useCaseMode === 'ai' ? 'AI Generated' : 'Manual'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="text-sm font-medium text-gray-600 mb-2">Content preview</p>
        <MarkdownRenderer content={useCaseContent} />
      </div>
    </div>
  );

  const isStep1Valid = selectedPromoter !== null;
  const isStep2Valid = true; // Review is optional
  const isStep3Valid = true; // mode selection only
  const trimmedManualBenefits = manualKeyBenefits.map((b) => b.trim()).filter(Boolean);
  const manualDescriptionWordCount = manualDescription.trim() ? manualDescription.trim().split(/\s+/).length : 0;
  const isManualFreeFormValid =
    manualTitle.trim().length > 0 &&
    manualDescription.trim().length > 0 &&
    manualDescriptionWordCount <= 12 &&
    trimmedManualBenefits.length >= 1 &&
    trimmedManualBenefits.every((b) => {
      const w = b.split(/\s+/).length;
      return w >= 1 && w <= 6;
    }) &&
    useCaseContent.trim().length > 0;
  const isStep4Valid = useCaseMode === 'manual' ? isManualFreeFormValid : useCaseContent.trim().length > 0;

  const canProceedStep4 = useCaseMode === 'manual' ? isStep4Valid : useCaseContent.length > 0 && hasGeneratedContent;

  return (
    <div className="space-y-8">
      {renderStepIndicator()}

      {error && currentStep !== 4 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {selectedPromoter && atUseCaseLimit && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-amber-800 text-sm">
            {t('useCases.requestPage.atLimit')}
          </p>
        </div>
      )}

      <div className="min-h-96">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderStep5()}
      </div>

      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={currentStep === 1 ? onClose : handlePrevious}
          className="flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          {currentStep === 1 ? (
            'Cancel'
          ) : (
            <>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </>
          )}
        </button>

        {currentStep < TOTAL_STEPS ? (
          <button
            type="button"
            onClick={currentStep === 5 ? undefined : handleNext}
            disabled={
              (currentStep === 1 && (!isStep1Valid || atUseCaseLimit)) ||
              (currentStep === 2 && !isStep2Valid) ||
              (currentStep === 3 && !isStep3Valid) ||
              (currentStep === 4 && !canProceedStep4)
            }
            className="flex items-center btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!useCaseContent.trim() || isSubmitting || atUseCaseLimit}
            className="flex items-center btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3"
          >
            {isSubmitting ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Send Request
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default UseCaseRequestForm;